import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { assert, assertExists } from 'common';
import { SearchPrompt } from 'models';
import { NGXLogger } from 'ngx-logger';
import { decodeBase64, encodeBase64 } from 'src/app/common/utility';
import { ApplicationQuery } from 'src/app/state/application/application.query';

/**
 * Session storage key for storing redirect URLs.
 */
export const REDIRECT_URLS_STORAGE_KEY = 'redirect.returnUrls';

type SupportedRoutes = 'viewer' | 'direct' | 'results' | 'select';

@Component({
  selector: 'app-gsw-redirect',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '', // This component does not have a template as it handles redirection logic and currently we don't need to display anything.
})
export class GswRedirectComponent implements OnInit {
  // The decoded data from the request.
  decodedData: Record<string, string | string[]> = {};
  // The route we are handling.
  routeName?: SupportedRoutes;
  // The encoded URL we came from.
  encodedData: string;
  // The URL we will redirect to after processing the request.
  targetUrl?: string;
  // Current requested route parameters.
  paramMap?: ParamMap;
  // The time query parameter from the request, used for session storage.
  requestTime: string;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private appQuery = inject(ApplicationQuery);
  private logger = inject(NGXLogger);

  ngOnInit(): void {
    this.parseRequestForRedirect();
  }

  /**
   * Parses the request for redirection and handles the routing logic based on
   * the decoded data.
   */
  private parseRequestForRedirect() {
    this.route.queryParamMap.subscribe((paramMap) => {
      this.paramMap = paramMap;
      this.encodedData = paramMap.get('request') ?? '';
      this.requestTime = paramMap.get('time') || Date.now().toString();
      // First check if this redirect was already performed via session storage.
      if (
        this.requestTime &&
        sessionStorage.getItem(REDIRECT_URLS_STORAGE_KEY)
      ) {
        const redirectReturnUrls = JSON.parse(
          sessionStorage.getItem(REDIRECT_URLS_STORAGE_KEY) || '{}'
        );
        // Check if the time exists in the session storage.
        if (redirectReturnUrls[this.requestTime]) {
          this.logger.info(
            'Returned to redirect for same request, redirecting to previously stored URL:',
            redirectReturnUrls[this.requestTime]
          );
          // Copy the URL from session storage, before clearing it.
          const returnUrl = redirectReturnUrls[this.requestTime];
          // Remove the stored URL from session storage to prevent future redirects.
          delete redirectReturnUrls[this.requestTime];
          sessionStorage.setItem(
            REDIRECT_URLS_STORAGE_KEY,
            JSON.stringify(redirectReturnUrls)
          );
          // If it exists, redirect to the stored URL.
          window.location.href = returnUrl;
          return;
        }
      }
      // If not a repeated request, parse the request and redirect accordingly.
      if (this.encodedData) {
        try {
          // Decode the base64 encoded request parameter.
          // This should be a base64 encoded string of the URL.
          this.parseRequestedUrl(this.encodedData);
          // Validate the route name and parameters.
          switch (this.routeName) {
            case 'viewer':
              // Viewer specific routing logic.
              this.handleViewer();
              break;
            case 'direct':
              // Direct access routing logic.
              this.handleDirect();
              break;
            case 'results':
              // Results routing logic.
              this.handleSearchResults();
              break;
            case 'select':
              // Database select, send to root.
              this.targetUrl = '/';
              break;
            default:
              // Unknown route.
              throw new Error(`Unknown route: ${this.routeName}`);
          }
          // If we have a target URL, redirect to it.
          this.redirectToTarget();
        } catch (e: unknown) {
          this.decodedData = {
            error: 'Invalid redirection request.',
            details: (e as Error)?.message ?? 'Unknown error',
          };
          this.logger.error(
            'Error processing redirection request:',
            this.decodedData
          );
          // Redirect to application root.
          this.targetUrl = '/';
          this.redirectToTarget();
        }
      }
    });
  }

  /**
   * Redirects to the target URL if it is defined.
   */
  private redirectToTarget() {
    assertExists(this.targetUrl, 'Target URL must be defined for redirection.');
    // Add the previous URL to the history for the user to go back to.
    this.saveReturnUrlToStorage();
    // Check if an instance is set in the application state.
    this.appendInstanceUrlIfNoneActive();
    this.logger.debug('Redirecting to target URL:', this.targetUrl);
    // Redirect to the target URL.
    window.history.pushState({}, '', location.href);
    this.router.navigateByUrl(this.targetUrl, {
      replaceUrl: false,
      skipLocationChange: false,
      info: 'redirect',
    });
  }

  /**
   * Saves the return URL to session storage for later use.
   * This is used to handle back navigation and prevent infinite redirects.
   * The return URL is saved with a 'noredirect' query parameter to prevent
   * the redirect logic from being triggered again when navigating back.
   * The return URL is saved in a map keyed by the time query parameter.
   * If no time is provided, the current timestamp is used as the key.
   * This allows the application to handle multiple redirects and back navigation
   * without losing the context of the original request.
   */
  private saveReturnUrlToStorage() {
    let returnUrl = decodeBase64(this.encodedData);
    this.logger.debug('Saving return URL to session storage:', returnUrl);
    returnUrl += (returnUrl.includes('?') ? '&' : '?') + 'noredirect=1';
    const sanitizedReturnUrl = sanitizeUrl(returnUrl.toString());
    this.logger.debug('Sanitized return URL:', sanitizedReturnUrl);
    // save the return URL in a map in session storage, keyed by the time query parameter.
    const redirectReturnUrls = JSON.parse(
      sessionStorage.getItem(REDIRECT_URLS_STORAGE_KEY) || '{}'
    );
    // Use the time query parameter as the key.
    if (this.requestTime) {
      redirectReturnUrls[this.requestTime] = sanitizedReturnUrl;
    } else {
      // If no time is provided, use the current timestamp as the key.
      const currentTime = Date.now().toString();
      // Set the time query parameter to the current url incase of back navigation.
      this.router.navigate([], {
        queryParams: { time: currentTime },
        queryParamsHandling: 'merge',
      });
      // Sanitize the return URL before saving it to the map.
      redirectReturnUrls[currentTime] = sanitizedReturnUrl;
    }
    sessionStorage.setItem(
      REDIRECT_URLS_STORAGE_KEY,
      JSON.stringify(redirectReturnUrls)
    );
  }

  /**
   * If the target URL is not defined, we should try and get the instance from
   * the url in an instance query string parmeter.
   */
  private appendInstanceUrlIfNoneActive() {
    const isInstanceSet = !!this.appQuery.instanceUrl;
    // Check if the requested url provided one in the query parameters.
    assertExists(this.paramMap, 'ParamMap must be defined.');
    assertExists(this.targetUrl, 'Target URL must be defined.');
    const sourceInstance = this.paramMap.get('instance');
    if (!isInstanceSet && sourceInstance) {
      // If the instance is not set, we should add the "instance" query
      // parameter to the target URL, accounting for if it already has query
      // parameters.
      if (sourceInstance) {
        const url = new URL(this.targetUrl, window.location.origin);
        // Add the instance URL as a query parameter.
        url.searchParams.set('instance', sourceInstance);
        this.targetUrl = url.pathname.toString() + url.search.toString();
      }
    }
  }

  /**
   * Handle the search results route.
   */
  private handleSearchResults() {
    // The results route requires a specific structure.
    // The path should be of the form: /results/{searchHash}?db={dbId}&archive={archiveId}&id={searchId}&criteria={criteria}
    assert(
      this.decodedData.path.length === 2,
      'Incorrect required route parameters for search results redirect.'
    );
    // Search hash (secureId) is used in place of a search Id.
    const searchHash = this.decodedData.path[1]; // SHA256 hash (secureId)
    // We require all of the following parameters to be present to be able to
    // redirect to the search results.
    assertExists(searchHash, 'Hash parameter is required (searchHash).');
    assertExists(this.decodedData.db, 'Database ID is required (db).');
    assertExists(this.decodedData.archive, 'Archive ID is required (archive).');
    assertExists(this.decodedData.id, 'Search ID is required (id).');
    // Criteria is optional, but if it exists, it should be a string, and we
    // will need to parse it as if it were javascript into an object.
    const prompts: SearchPrompt[] = [];
    if (this.decodedData.criteria) {
      const criteria = this.parseCriteria(this.decodedData.criteria);
      // If criteria is valid, convert it to SearchPrompt objects.
      // It should work sparse like this with only the keys that are present and
      // no prompt text.
      prompts.push(
        ...Object.entries(criteria).map(
          ([key, value]) => ({ id: Number(key), value }) as SearchPrompt
        )
      );
    }
    // Construct the URL for the search results page, the criteria are sent as
    // prompts (base64 encoded JSON).
    this.targetUrl = `/db/${this.decodedData.db}/archive/${this.decodedData.archive}/search/${this.decodedData.id}`;
    if (prompts.length > 0) {
      this.targetUrl += `?prompts=${encodeBase64(JSON.stringify(prompts))}`;
    }
  }

  private parseCriteria(criteria: Record<number, string>) {
    try {
      const jsonString = (this.decodedData.criteria as string).replace(
        /([{,]\s*)(\d+)\s*:/g,
        '$1"$2":'
      );
      const parsed = JSON.parse(jsonString);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        Object.keys(parsed).every((k) => !isNaN(Number(k))) &&
        Object.values(parsed).every((v) => typeof v === 'string')
      ) {
        criteria = parsed;
      } else {
        throw new Error(
          'Criteria must be an object with number keys and string values.'
        );
      }
    } catch (e) {
      throw new Error('Invalid criteria format. Unable to parse.');
    }
    return criteria;
  }

  /**
   * Handle the direct route.
   */
  private handleDirect() {
    // The direct route requires a specific structure.
    // The path should be of the form: /direct?db={dbId}&a={archiveId}&d={databaseId}
    assert(
      this.decodedData.path.length === 1,
      'Incorrect required route parameters for direct redirect.'
    );
    // We require all of the following parameters to be present to be able to
    // redirect to the viewer.
    assertExists(this.decodedData.db, 'Database ID is required (db).');
    assertExists(this.decodedData.a, 'Archive ID is required (a).');
    assertExists(this.decodedData.d, 'Document ID is required (d).');
    // Construct the URL for the direct access page.
    this.targetUrl = `/db/${this.decodedData.db}/archive/${this.decodedData.a}/direct/document?documentid=${this.decodedData.d}`;
  }

  /**
   * Handles the viewer route.
   */
  private handleViewer() {
    // The viewer route requires a specific structure.
    // The path should be of the form: /viewer/{documentHash}?db={dbId}&archive={archiveId}&id={documentId}
    assert(
      this.decodedData.path.length === 2,
      'Incorrect required route parameters for viewer redirect.'
    );
    // Document hash (secureId) is used in place of a search Id.
    const documentHash = this.decodedData.path[1]; // SHA256 hash of {archiveid}_{documentid}{token}.
    // We require all of the following parameters to be present to be able to redirect to the viewer.
    assertExists(documentHash, 'Hash parameter is required (documentHash).');
    assertExists(this.decodedData.db, 'Database ID is required (db).');
    assertExists(this.decodedData.archive, 'Archive ID is required (archive).');
    assertExists(this.decodedData.id, 'Document ID is required (id).');
    // Construct the URL for the viewer.
    this.targetUrl = `/db/${this.decodedData.db}/archive/${this.decodedData.archive}/document/${this.decodedData.id}|${documentHash}`;
  }

  private parseRequestedUrl(encoded: string) {
    const requestedUrl = decodeBase64(encoded);
    // Strip everything before # if it exists
    const hashIndex = requestedUrl.indexOf('#');
    const cleanUrl =
      hashIndex !== -1 ? requestedUrl.substring(hashIndex + 1) : requestedUrl;
    // Parse the url segments and parameters into an object structure
    // Seperate the urls path paramaters from the query parameters
    const url = new URL(cleanUrl, window.location.origin);
    // Assign the path segments to decodedData
    this.decodedData.path = url.pathname
      .toLowerCase()
      .split('/')
      .filter(Boolean);
    // Handle query parameters after the ?
    const urlParams = new URLSearchParams(url.search);
    // Convert URLSearchParams to a simple object
    urlParams.forEach((value, key) => {
      this.decodedData[key.toLowerCase()] = value;
    });
    this.routeName = this.decodedData.path[0] as SupportedRoutes;
    this.logger.debug(
      'Parsed request for redirection:',
      this.decodedData,
      'Route Name:',
      this.routeName
    );
  }
}
